import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { fetchUuidRegisterForScope } from "../../DCSTab/UUIDRegister/uuidRegisterApi";
import { formatUidDisplay } from "../../DCSTab/UUIDRegister/uuidRegisterUtils";
import { resolveScheduleTaskType } from "../../../../Project/plant/PlantTaskPage/TaskList/components/constants";
import { formatResourceLabel } from "../../Resources/buildResourceColumn";
import {
  buildExistingGroupNames,
  buildGroupNameToPrefix,
  computeTaskCode,
  EMPTY_NEW_TASK,
  validateNewGroupCode,
} from "./createTaskDialogUtils";

const TYPE_OPTIONS = [
  { value: "engineering", label: "EMS (Engineering)" },
  { value: "procurement", label: "PMS (Procurement)" },
  { value: "fabrication", label: "CMS (Fabrication)" },
  { value: "itp", label: "ITP" },
];

export default function CreateTaskDialog({
  open,
  onClose,
  onCreated,
  tasks = [],
  defaultType = "engineering",
  lockType = false,
  plant = true,
  plantId,
  projectId,
  accessToken,
  user,
  title = "Create Task",
  onSubmit,
  resources = [],
}) {
  const [creating, setCreating] = useState(false);
  const [newTask, setNewTask] = useState({
    ...EMPTY_NEW_TASK,
    type: resolveScheduleTaskType(defaultType),
  });
  const [wbsMode, setWbsMode] = useState("existing");
  const [wbsSuffix, setWbsSuffix] = useState("");
  const [wbsFull, setWbsFull] = useState("");
  const [groupName, setGroupName] = useState("");
  const [selectedUid, setSelectedUid] = useState("");
  const [uuidChoices, setUuidChoices] = useState([]);
  const [uuidChoicesLoading, setUuidChoicesLoading] = useState(false);

  const prevOpenRef = useRef(false);

  const groupNameToPrefix = useMemo(
    () => buildGroupNameToPrefix(tasks),
    [tasks],
  );

  const existingGroupNames = useMemo(
    () => buildExistingGroupNames(tasks),
    [tasks],
  );

  const existingCodePrefixes = useMemo(
    () => Array.from(groupNameToPrefix.values()),
    [groupNameToPrefix],
  );

  const autoCodePrefix = groupName
    ? groupNameToPrefix.get(groupName) || ""
    : "";

  const computedCode = useMemo(() => {
    if (wbsMode === "existing") {
      const suffix = String(wbsSuffix || "")
        .trim()
        .replace(/^\.+/, "");
      const prefix = autoCodePrefix;
      if (!prefix || !suffix) return "";
      return `${prefix}.${suffix}`;
    }
    return String(wbsFull || "").trim();
  }, [wbsMode, autoCodePrefix, wbsSuffix, wbsFull]);

  const computedTaskCode = useMemo(
    () =>
      computeTaskCode({
        wbsMode,
        wbsSuffix,
        wbsFull,
        autoCodePrefix,
        groupName,
        selectedUid,
      }),
    [wbsMode, wbsSuffix, wbsFull, autoCodePrefix, groupName, selectedUid],
  );

  const newGroupError = useMemo(
    () => validateNewGroupCode(wbsMode, wbsFull, existingCodePrefixes),
    [wbsMode, wbsFull, existingCodePrefixes],
  );

  const resetForm = (preferExistingGroup = existingGroupNames.length > 0) => {
    setNewTask({
      ...EMPTY_NEW_TASK,
      type: resolveScheduleTaskType(defaultType),
    });
    setWbsMode(preferExistingGroup ? "existing" : "new");
    setWbsSuffix("");
    setWbsFull("");
    setGroupName("");
    setSelectedUid("");
  };

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (justOpened) {
      resetForm(existingGroupNames.length > 0);
    }
  }, [open, existingGroupNames.length, defaultType]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;

    const loadUuidChoices = async () => {
      setUuidChoicesLoading(true);
      try {
        const { items } = await fetchUuidRegisterForScope({
          plant,
          plantId,
          activeProjectId: projectId,
        });
        if (cancelled) return;
        const seen = new Set();
        const choices = [];
        for (const row of items || []) {
          if (!row || row.archived) continue;
          const uid =
            String(row.uid ?? "").trim() ||
            formatUidDisplay(row.tag, row.registerNumber);
          if (!uid || seen.has(uid)) continue;
          seen.add(uid);
          choices.push(uid);
        }
        choices.sort((a, b) => a.localeCompare(b));
        setUuidChoices(choices);
      } catch (e) {
        console.warn("UUID register choices load failed:", e);
        if (!cancelled) setUuidChoices([]);
      } finally {
        if (!cancelled) setUuidChoicesLoading(false);
      }
    };

    loadUuidChoices();
    return () => {
      cancelled = true;
    };
  }, [open, plant, plantId, projectId]);

  const handleNewTaskChange = (field) => (event) => {
    const value = event.target.value;
    setNewTask((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    if (creating) return;
    onClose?.();
  };

  const handleCreate = async () => {
    const taskCode = computedTaskCode;
    const taskName = wbsMode === "new" ? groupName.trim() : newTask.task;
    if (!taskName || !taskCode || newGroupError || !onSubmit) return;

    try {
      setCreating(true);
      const loggedInUserName = user?.name ?? user?.email ?? "";
      const payload = {
        task: taskName,
        taskCode,
        type: newTask.type,
        startDate: newTask.startDate || null,
        endDate: newTask.endDate || null,
        estimatedTime: Number(newTask.estimatedTime) || 0,
        status: "Not Started",
        priority: "Medium",
        task_members: loggedInUserName || "",
        resourceId: newTask.resourceId || null,
      };

      await onSubmit(payload);

      const wasNewGroup = wbsMode === "new";
      const createdGroupName = groupName.trim();

      await onCreated?.();

      if (wasNewGroup && createdGroupName) {
        setWbsMode("existing");
        setGroupName(createdGroupName);
        setNewTask((prev) => ({
          ...EMPTY_NEW_TASK,
          type: resolveScheduleTaskType(defaultType),
        }));
        setWbsSuffix("");
        setWbsFull("");
        setSelectedUid("");
      } else {
        resetForm(existingGroupNames.length > 0);
        onClose?.();
      }
    } catch (e) {
      console.error("Error creating task:", e);
    } finally {
      setCreating(false);
    }
  };

  const canSubmit =
    !creating &&
    (wbsMode === "new" ? groupName.trim() : newTask.task) &&
    computedTaskCode &&
    !newGroupError &&
    String(selectedUid ?? "").trim() &&
    uuidChoices.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
      >
        <Autocomplete
          options={uuidChoices}
          value={selectedUid || null}
          onChange={(_, value) => setSelectedUid(value || "")}
          loading={uuidChoicesLoading}
          disabled={uuidChoicesLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Registered UID #"
              required
              size="small"
              placeholder={
                uuidChoicesLoading
                  ? "Loading UIDs…"
                  : uuidChoices.length === 0
                    ? "No UIDs in UID Register"
                    : "Select a UID"
              }
              helperText={
                !uuidChoicesLoading && uuidChoices.length === 0
                  ? "Add UIDs in the DCS UID Register first."
                  : undefined
              }
            />
          )}
        />
        {wbsMode !== "new" && (
          <TextField
            label="Task Name"
            value={newTask.task}
            onChange={handleNewTaskChange("task")}
            fullWidth
          />
        )}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            WBS ID (code-groupName, e.g. 2.1.1-group1)
          </Typography>
          <RadioGroup
            row
            value={wbsMode}
            onChange={(e) => setWbsMode(e.target.value)}
            sx={{ mb: 1 }}
          >
            {existingCodePrefixes.length > 0 && (
              <FormControlLabel
                value="existing"
                control={<Radio size="small" />}
                label="Use existing group"
              />
            )}
            <FormControlLabel
              value="new"
              control={<Radio size="small" />}
              label="New group"
            />
          </RadioGroup>
          {wbsMode === "existing" ? (
            <Stack spacing={1.5}>
              <Autocomplete
                options={existingGroupNames}
                value={groupName}
                onChange={(_, value) => {
                  setGroupName(value || "");
                  setWbsSuffix("");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Group (select first)"
                    size="small"
                    placeholder="Select group"
                  />
                )}
              />
              {groupName && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    label="Code prefix"
                    value={autoCodePrefix}
                    size="small"
                    sx={{ minWidth: 100 }}
                    InputProps={{ readOnly: true }}
                  />
                  <Typography color="text.secondary">.</Typography>
                  <TextField
                    label="Suffix *"
                    value={wbsSuffix}
                    onChange={(e) => setWbsSuffix(e.target.value)}
                    size="small"
                    placeholder="e.g. 1, 2, 3"
                    required
                  />
                </Stack>
              )}
              {groupName && wbsSuffix.trim() && (
                <Typography variant="caption" color="text.secondary">
                  Full WBS:{" "}
                  {computedCode ? `${computedCode}-${groupName}` : "—"}
                </Typography>
              )}
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <TextField
                label="Full code"
                value={wbsFull}
                onChange={(e) => setWbsFull(e.target.value)}
                fullWidth
                size="small"
                error={!!newGroupError}
                helperText={newGroupError}
                placeholder="e.g. 4.1, 5.2"
              />
              <TextField
                label="Group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g. group1, procurement"
              />
              {groupName && wbsFull && (
                <Typography variant="caption" color="text.secondary">
                  Full WBS: {wbsFull}-{groupName}
                </Typography>
              )}
            </Stack>
          )}
        </Box>
        <TextField
          select
          label="4D"
          value={newTask.type}
          onChange={handleNewTaskChange("type")}
          fullWidth
          disabled={lockType}
        >
          {TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction="row" spacing={2}>
          <TextField
            label="Baseline Start"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={newTask.startDate}
            onChange={handleNewTaskChange("startDate")}
            fullWidth
          />
          <TextField
            label="Baseline End"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={newTask.endDate}
            onChange={handleNewTaskChange("endDate")}
            fullWidth
          />
        </Stack>
        <TextField
          label="Budget Hours"
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          value={newTask.estimatedTime}
          onChange={handleNewTaskChange("estimatedTime")}
          fullWidth
        />
        <TextField
          select
          label="Resource"
          value={newTask.resourceId ?? ""}
          onChange={handleNewTaskChange("resourceId")}
          fullWidth
          size="small"
          helperText={
            resources.length === 0
              ? "Add resources via Manage Resources on the task table."
              : undefined
          }
        >
          <MenuItem value="">None</MenuItem>
          {resources.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {formatResourceLabel(r)}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={!canSubmit}>
          {creating ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
